'use client'

import React, { useMemo } from 'react'
import { cn } from '@/utilities/ui'
import { colorToBackgroundClass, type Color } from '@/utilities/colors'
import { getRandomShard } from './Pixelator/shards'

type ShardProps = {
  className?: string
  size?: number // Pixel size of the entire shard (defaults to 30).
  align?: 'center' | 'start'
  force3x3?: boolean
  shard?: {
    blocks: {
      x: number
      y: number
      color: {
        name: string
      }
    }[]
  }
}

export function Shard({
  className,
  size = 30,
  align = 'center',
  force3x3 = false,
  shard,
}: ShardProps) {
  const variant = useMemo(() => {
    return shard ?? getRandomShard()
  }, [shard])

  // Calculate grid dimensions based on blocks
  const dimensions = useMemo(() => {
    const maxX = Math.max(...variant.blocks.map((b) => b.x)) + 1
    const maxY = Math.max(...variant.blocks.map((b) => b.y)) + 1
    const cols = force3x3 ? 3 : maxX
    const rows = force3x3 ? 3 : maxY
    return { cols, rows }
  }, [variant, force3x3])

  // Create a grid array with empty cells
  const grid = useMemo(() => {
    const cells = Array<Color | null>(dimensions.rows * dimensions.cols).fill(null)
    variant.blocks.forEach((block) => {
      const idx = block.y * dimensions.cols + block.x
      cells[idx] = block.color.name as Color
    })
    return cells
  }, [variant, dimensions])

  const containerClasses = cn(
    'transition-transform duration-300 hover:scale-120 origin-center',
    align === 'center' ? 'flex items-center justify-center' : 'flex items-start justify-start',
    className,
  )

  return (
    <div
      className={containerClasses}
      style={{
        width: size,
        height: size,
      }}
    >
      <div
        className="grid w-fit"
        style={{
          gridTemplateColumns: `repeat(${dimensions.cols}, ${size / 3}px)`,
          gridTemplateRows: `repeat(${dimensions.rows}, ${size / 3}px)`,
        }}
        aria-hidden="true"
        suppressHydrationWarning
      >
        {grid.map((color, i) => (
          <div
            key={i}
            className={cn(color ? colorToBackgroundClass[color] : null)}
            suppressHydrationWarning
          />
        ))}
      </div>
    </div>
  )
}
